/**
 * AnomalieBadge — alertes d'anomalies contextuelles (lot 1, 11/06/2026).
 *
 * Pattern Rapport Bijoux : badge ⚠ + tooltip explication + actions.
 * JAMAIS de correction silencieuse : « Vu, normal » marque (anomalies_statuts),
 * la donnée reste. §205 : ne rend rien hors rôle central (admin).
 *
 * Deux usages :
 *  - <AnomalieBadge type="A4" items={[...]} explication titre onChange />  (en-tête de page)
 *  - <AnomalieDot item={...} type onChange />                              (sur une ligne)
 */
import React, { useState } from 'react';
import { API_URL } from '../../services/api';

const headers = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` });

export const estCentral = () => {
  try { return (JSON.parse(localStorage.getItem('user') || '{}')?.role || '') === 'admin'; }
  catch { return false; }
};

export const chargerAnomalies = async (types) => {
  // -> { A4: {titre, explication, items:[{entite_id, libelle, vu, commentaire_vu}]}, ... }
  const out = {};
  await Promise.all(types.map(async (t) => {
    try {
      const r = await fetch(`${API_URL}/anomalies/liste/${t}`, { headers: headers() });
      if (r.ok) out[t] = await r.json();
    } catch (e) { /* non bloquant */ }
  }));
  return out;
};

export const marquerVu = async (type, entiteId, commentaire) => {
  const r = await fetch(`${API_URL}/anomalies/${type}/vu`, {
    method: 'POST', headers: headers(),
    body: JSON.stringify({ entite_id: entiteId, commentaire: commentaire || null }),
  });
  return r.ok;
};

export const annulerVu = async (type, entiteId) => {
  const r = await fetch(`${API_URL}/anomalies/${type}/vu/${entiteId}`, { method: 'DELETE', headers: headers() });
  return r.ok;
};

/** Badge d'en-tête : compteur + panneau dépliable listant les cas. */
export function AnomalieBadge({ type, titre, explication, items = [], onChange }) {
  const [ouvert, setOuvert] = useState(false);
  const [enCours, setEnCours] = useState(null);
  if (!estCentral()) return null;
  const actifs = items.filter(i => !i.vu);
  if (items.length === 0) return null;

  const vu = async (it) => {
    setEnCours(it.entite_id);
    const motif = window.prompt(`« Vu, normal » — ${it.libelle}\nCommentaire (optionnel) :`, '');
    if (motif !== null) { await marquerVu(type, it.entite_id, motif); onChange && onChange(); }
    setEnCours(null);
  };

  return (
    <div className="relative inline-block">
      <button onClick={() => setOuvert(o => !o)}
        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
          actifs.length > 0 ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                            : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'}`}
        title={explication}>
        ⚠ {actifs.length > 0 ? `${actifs.length} ${titre}` : `${titre} : tout vu`}
      </button>
      {ouvert && (
        <div className="absolute z-50 mt-1 left-0 w-96 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl p-3">
          <p className="text-xs font-bold text-gray-700 mb-1">{titre} ({type})</p>
          <p className="text-xs text-gray-500 mb-2">{explication}</p>
          {items.map(it => (
            <div key={it.entite_id}
              className={`flex items-center justify-between gap-2 py-1.5 border-t border-gray-100 text-xs ${it.vu ? 'opacity-50' : ''}`}>
              <span className="text-gray-700">{it.libelle}{it.vu && <em className="text-gray-400"> — vu{it.commentaire_vu ? ` (${it.commentaire_vu})` : ''}</em>}</span>
              {it.vu ? (
                <button onClick={async () => { await annulerVu(type, it.entite_id); onChange && onChange(); }}
                  className="shrink-0 text-gray-400 hover:text-gray-700 underline">réactiver</button>
              ) : (
                <button disabled={enCours === it.entite_id} onClick={() => vu(it)}
                  className="shrink-0 text-emerald-700 hover:text-emerald-900 underline">vu, normal</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Pastille de ligne : ⚠ sur l'élément concerné, tooltip + « vu, normal ». */
export function AnomalieDot({ type, item, onChange }) {
  if (!estCentral() || !item || item.vu) return null;
  return (
    <button
      onClick={async (e) => {
        e.stopPropagation();
        const motif = window.prompt(`Anomalie ${type} — ${item.libelle}\n« Vu, normal » ? Commentaire (optionnel) :`, '');
        if (motif !== null) { await marquerVu(type, item.entite_id, motif); onChange && onChange(); }
      }}
      className="ml-1 text-amber-500 hover:text-amber-700"
      title={`${item.libelle} — cliquer pour marquer « vu, normal »`}>
      ⚠
    </button>
  );
}

export default AnomalieBadge;
